import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';
import {
  OperatorGroupDto,
  CreateOperatorGroupRequest,
  UpdateOperatorGroupRequest,
  OperatorInGroupDto,
  AssignOperatorToGroupRequest,
  RemoveOperatorFromGroupRequest,
  OperatorBasicDto
} from '../types/operatorGroup';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';
const BASE_URL = `${API_BASE_URL}/operator-groups`;

/**
 * Service for OperatorGroup and membership operations
 */
export const operatorGroupService = {
  // OperatorGroup CRUD
  async getAllGroups(): Promise<OperatorGroupDto[]> {
    const response = await AuthService.authenticatedGet(BASE_URL);
    if (!response.ok) {
      throw new Error(`Failed to get operator groups: ${response.status}`);
    }
    return response.json();
  },

  async getGroupById(operatorGroupId: string): Promise<OperatorGroupDto> {
    const response = await AuthService.authenticatedGet(`${BASE_URL}/${operatorGroupId}`);
    if (!response.ok) {
      throw new Error(`Failed to get operator group: ${response.status}`);
    }
    return response.json();
  },

  async createGroup(request: CreateOperatorGroupRequest): Promise<OperatorGroupDto> {
    const response = await AuthService.authenticatedPost(BASE_URL, request);
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to create operator group'));
    }
    return response.json();
  },

  async updateGroup(operatorGroupId: string, request: UpdateOperatorGroupRequest): Promise<OperatorGroupDto> {
    const response = await AuthService.authenticatedPut(`${BASE_URL}/${operatorGroupId}`, request);
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to update operator group'));
    }
    return response.json();
  },

  async deleteGroup(operatorGroupId: string): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${BASE_URL}/${operatorGroupId}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to delete operator group'));
    }
  },

  // Membership operations
  async getOperatorGroups(operatorId: string): Promise<OperatorInGroupDto[]> {
    const response = await AuthService.authenticatedGet(`${BASE_URL}/memberships/by-operator/${operatorId}`);
    if (!response.ok) {
      throw new Error(`Failed to get operator groups: ${response.status}`);
    }
    return response.json();
  },

  async getGroupMembers(operatorGroupId: string): Promise<OperatorInGroupDto[]> {
    const response = await AuthService.authenticatedGet(`${BASE_URL}/${operatorGroupId}/members`);
    if (!response.ok) {
      throw new Error(`Failed to get group members: ${response.status}`);
    }
    return response.json();
  },

  async assignToGroup(request: AssignOperatorToGroupRequest): Promise<OperatorInGroupDto> {
    const response = await AuthService.authenticatedPost(`${BASE_URL}/memberships`, request);
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to assign operator to group'));
    }
    return response.json();
  },

  async removeFromGroup(request: RemoveOperatorFromGroupRequest): Promise<void> {
    const response = await AuthService.authenticatedFetch(`${BASE_URL}/memberships`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(request)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to remove operator from group'));
    }
  },

  async setOperatorGroups(operatorId: string, operatorGroupIds: string[]): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${BASE_URL}/memberships/by-operator/${operatorId}`,
      operatorGroupIds
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to set operator groups'));
    }
  },

  // Operator lookup
  async getAllOperators(): Promise<OperatorBasicDto[]> {
    const response = await AuthService.authenticatedGet(`${BASE_URL}/operators`);
    if (!response.ok) {
      throw new Error(`Failed to get operators: ${response.status}`);
    }
    return response.json();
  }
};
